import React, { useCallback, useEffect, useState } from "react";
import { ArrowBack, DeleteOutline } from "@mui/icons-material";
import { Alert, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import api from "services/api";
import StatusChip from "../components/StatusChip";
// import { mockAcceptances, mockApprovals, mockDocuments, mockHistory, mockItems, mockPayments, mockPersonnel, mockSections, mockSubcontracts } from "../data/mockData";
import { fetchPersonnel } from "modules/systems/services/personnel/personnel.service";
import { isAdminUser } from "modules/systems/services/application.service";
import { AppButton } from "components/common/buttons";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { safely, subcontractApi } from "../services/subcontractApi";
import InformationTab from "../tabs/InformationTab";
import ItemsTab from "../tabs/ItemsTab";
import PaymentsTab from "../tabs/PaymentsTab";
import AcceptancesTab from "../tabs/AcceptancesTab";
import ApprovalsTab from "../tabs/ApprovalsTab";
import HistoryTab from "../tabs/HistoryTab";
import ContractStatisticsPanel from "../components/statistics/ContractStatisticsPanel";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";

const tabs = ["Thông tin", "Dự toán", "Phê duyệt", "Nghiệm thu", "Thanh toán", "Thống kê", "Lịch sử", "Phê duyệt nghiệm thu"];

const normalizeStatus = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const mergeTeamWithPersonnel = (teamMembers = [], personnelList = []) => {
  const personnelById = new Map(
    personnelList
      .filter((personnel) => personnel?.personnelId != null)
      .map((personnel) => [personnel.personnelId, personnel]),
  );

  return teamMembers
    .map((teamMember) => {
      const personnelId = teamMember.personnelId ?? teamMember.userId;
      const personnelInfo = personnelById.get(personnelId) || {};
      const fullName = personnelInfo.fullName || teamMember.fullName || teamMember.userName;

      return {
        ...personnelInfo,
        ...teamMember,
        personnelId,
        fullName,
        userName: teamMember.userName || fullName,
        title: teamMember.title || personnelInfo.title,
        departmentName: teamMember.departmentName || personnelInfo.departmentName,
        enterpriseName: teamMember.enterpriseName || personnelInfo.enterpriseName,
        roleInSubcontract: teamMember.roleInSubcontract || teamMember.RoleInSubcontract,
        displayOrder: teamMember.displayOrder || 0,
      };
    })
    .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
};

export default function SubcontractDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = useSubcontractPermissions();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  // const fallback = mockSubcontracts.find((item) => String(item.subcontractId) === String(id)) || mockSubcontracts[0];
  const [contract, setContract] = useState(null);
  const [error, setError] = useState("");
  const [allowedSubcontractIds, setAllowedSubcontractIds] = useState(null);
  const [related, setRelated] = useState({
    personnel: [], documents: [], sections: [], items: [], draftItems: [],
    payments: [], acceptances: [], approvals: [],
    history: { contract: [], status: [], items: [], approvals: [] },
  });

  const loadDetail = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const contractData = await safely(() => subcontractApi.getSubcontract(id));
      const [teamMembers, personnelDirectory, documents, sections, items, draftItems, payments, acceptances, approvals, contractHistory, statusHistory, itemHistory, approvalHistory, acceptanceApprovalHistory, assignee] = await Promise.all([
        safely(() => subcontractApi.getTeam(id)),
        safely(() => fetchPersonnel(1000, 1, "", "displayOrder", "ASC")),
        safely(() => subcontractApi.getDocuments(id)),
        safely(() => subcontractApi.getSections(id)),
        safely(() => subcontractApi.getItems(id)),
        safely(() => subcontractApi.getDraftItems(id)),
        safely(() => subcontractApi.getPaymentProfiles(id)),
        safely(() => subcontractApi.getAcceptanceProfiles(id)),
        safely(() => subcontractApi.getApprovalProfilesBySubcontract(id)),
        safely(() => subcontractApi.getContractHistory(id)),
        safely(() => subcontractApi.getStatusHistory(id)),
        safely(() => subcontractApi.getItemHistory(id)),
        safely(() => subcontractApi.getApprovalHistory(id)),
        safely(() => subcontractApi.getAcceptanceApprovalHistory(id)),
        subcontractApi.getSubcontractAssignee(id).catch(() => null),
      ]);

      const personnel = mergeTeamWithPersonnel(
        teamMembers,
        Array.isArray(personnelDirectory?.items) ? personnelDirectory.items : [],
      );

      setContract({
        ...contractData,
        assignee,
        subcontractorName: assignee?.assigneeName || contractData?.subcontractorName,
      });
      setRelated({
        personnel, documents, sections, items, draftItems, payments, acceptances, approvals,
        history: {
          contract: contractHistory,
          status: statusHistory,
          items: itemHistory,
          approvals: approvalHistory,
          acceptanceApprovals: acceptanceApprovalHistory,
        },
      });
      setError("");
    } catch (requestError) {
      setContract(null);
      setError(requestError.response?.data?.message || requestError.message || "Không thể tải chi tiết giao khoán từ backend.");
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    let active = true;

    const loadAccessibleSubcontracts = async () => {
      if (!user?.userId) {
        if (active) setAllowedSubcontractIds("none");
        return;
      }

      if (isAdminUser(user)) {
        if (active) setAllowedSubcontractIds("all");
        return;
      }

      try {
        const response = await api.get(`/UserSubcontract/user/${user.userId}`, {
          params: { pageSize: 1000, pageNumber: 1 },
        });
        const entries = response?.data?.data || [];
        const ids = entries
          .map((item) => item.subcontractId ?? item.SubcontractId)
          .filter((value) => value != null);

        if (active) setAllowedSubcontractIds(ids.length > 0 ? ids : "none");
      } catch (requestError) {
        if (active) {
          setAllowedSubcontractIds("error");
          setError(
            requestError.response?.data?.message ||
            requestError.message ||
            "KhÃ´ng thá»ƒ táº£i danh sÃ¡ch phÃ¢n quyá»n giao khoÃ¡n.",
          );
        }
      }
    };

    setAllowedSubcontractIds(null);
    loadAccessibleSubcontracts();

    return () => {
      active = false;
    };
  }, [user]);

  const visibleTabs = [
    { key: "info", label: tabs[0] },
    { key: "items", label: tabs[1] },
    ...(permissions.has(SUBCONTRACT_PERMISSIONS.approvalRead) ? [{ key: "approvals", label: tabs[2] }] : []),
    ...(permissions.canReadAcceptance ? [{ key: "acceptances", label: tabs[3] }] : []),
    ...(permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceApprovalRead) ? [{ key: "acceptance-approvals", label: tabs[7] }] : []),
    ...(permissions.has(SUBCONTRACT_PERMISSIONS.paymentRead) ? [{ key: "payments", label: tabs[4] }] : []),
    { key: "statistics", label: tabs[5] },
    { key: "history", label: tabs[6] },
  ];
  const activeTab = visibleTabs[tab]?.key || visibleTabs[0]?.key;
  const isCancelled = normalizeStatus(contract?.status).includes("huy bo");
  // === [OLD CODE - Soft Delete: không cho xóa khi đã hủy bỏ] ===
  // const canDeleteSubcontract = permissions.has(SUBCONTRACT_PERMISSIONS.subcontractDelete) && !isCancelled;
  // === [END OLD CODE] ===
  // === [NEW CODE - Hard Delete: cho phép xóa ở mọi trạng thái] ===
  const canDeleteSubcontract = permissions.has(SUBCONTRACT_PERMISSIONS.subcontractDelete);
  // === [END NEW CODE] ===

  const closeSnackbar = () => {
    setSnackbar((current) => ({ ...current, open: false }));
  };

  const executeDeleteSubcontract = async () => {
    if (!contract || deleting) return;

    closeSnackbar();
    setDeleting(true);
    setError("");

    try {
      await subcontractApi.deleteSubcontract(contract.subcontractId ?? id);
      setConfirmDialogOpen(false);
      setSnackbar({ open: true, message: "Xóa giao khoán thành công.", severity: "success" });
      navigate("/subcontracts/contracts");
    } catch (requestError) {
      setConfirmDialogOpen(false);
      setSnackbar({
        open: true,
        severity: "error",
        message:
          requestError.response?.data?.message ||
          requestError.message ||
          "Không thể xóa giao khoán.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSubcontract = () => {
    if (!contract || deleting) return;
    setConfirmDialogOpen(true);
  };

  if (loading || permissions.loading) return <Box className="subcontract-page" sx={{ display: "grid", placeItems: "center", minHeight: 500 }}><CircularProgress /></Box>;
  if (error || !contract) return <Box className="subcontract-page"><Alert severity="error">{error || "Không tìm thấy quyết định giao khoán."}</Alert></Box>;

  return (
    <Box className="subcontract-page">
      <Box className="subcontract-detail-hero">
        <Box className="subcontract-detail-hero__left">
          <AppButton
            size="small"
            color="inherit"
            onClick={() => navigate("/subcontracts/contracts")}
            sx={{ minWidth: 20, width: 20, height: 20, px: 0, border: "none" }}>
            <ArrowBack sx={{ fontSize: 20 }} />
          </AppButton>
          <Box className="subcontract-detail-hero__title">
            <Typography component="span" fontWeight={750}>{contract.subcontractNumber}</Typography>
            <Typography component="span" color="text.secondary">-</Typography>
            <Typography component="span" color="text.secondary">{contract.subcontractName}</Typography>
          </Box>
        </Box>
        <Box className="subcontract-detail-hero__statuses">
          <StatusChip value={contract.status} />
          { /* <StatusChip value={contract.approvalStatus || "Tạo mới"} /> */}
          {canDeleteSubcontract && (
            <Tooltip title="Xóa giao khoán">
              <span>
                <AppButton
                  className="subcontract-detail-hero__delete"
                  size="small"
                  variant="outlined"
                  startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline fontSize="small" />}
                  color="error"
                  disabled={deleting}
                  onClick={handleDeleteSubcontract}
                >
                  {/* {deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutline fontSize="small" />} */}
                  Xóa giao khoán
                </AppButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Tabs value={Math.min(tab, visibleTabs.length - 1)} onChange={(_, value) => setTab(value)} className="subcontract-main-tabs" variant="scrollable">
        {visibleTabs.map((item) => <Tab key={item.key} label={item.label} />)}
      </Tabs>

      <Box className="subcontract-detail-content">
        {activeTab === "info" && <InformationTab contract={contract} personnel={related.personnel} documents={related.documents} onChanged={() => loadDetail({ silent: true })} />}
        {activeTab === "items" && <ItemsTab subcontractId={id} subcontract={contract} sections={related.sections} payments={related.payments} approvals={related.approvals} onChanged={() => loadDetail({ silent: true })} />}
        {activeTab === "payments" && <PaymentsTab subcontractId={id} subcontract={contract} payments={related.payments} items={related.items} sections={related.sections} subcontractApprovalProfiles={related.approvals} onChanged={() => loadDetail({ silent: true })} />}
        {activeTab === "acceptances" && <AcceptancesTab subcontractId={id} subcontract={contract} acceptances={related.acceptances} items={related.items} sections={related.sections} subcontractApprovalProfiles={related.approvals} onChanged={() => loadDetail({ silent: true })} />}
        {activeTab === "acceptance-approvals" && <AcceptancesTab viewMode="approval" subcontractId={id} subcontract={contract} acceptances={related.acceptances} items={related.items} sections={related.sections} subcontractApprovalProfiles={related.approvals} onChanged={() => loadDetail({ silent: true })} />}
        {activeTab === "approvals" && <ApprovalsTab subcontractId={id} subcontract={contract} approvals={related.approvals} items={related.draftItems} officialItems={related.items} sections={related.sections} onChanged={() => loadDetail({ silent: true })} />}
        {activeTab === "history" && <HistoryTab history={related.history} />}
        {activeTab === "statistics" && (
          <ContractStatisticsPanel
            contractId={contract.contractId}
            allowedSubcontractIds={allowedSubcontractIds}
          />
        )}
      </Box>
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        action={snackbar.action}
        autoHideDuration={snackbar.autoHideDuration}
        onClose={closeSnackbar}
      />
      <Dialog
        open={confirmDialogOpen}
        onClose={() => !deleting && setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }} color="error">Xác nhận xóa giao khoán</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Bạn có chắc chắn muốn xóa vĩnh viễn giao khoán{" "}
            <strong>
              {contract?.subcontractNumber || contract?.subcontractName || "này"}
            </strong>
            ? Dữ liệu sẽ bị xóa hoàn toàn và không thể khôi phục.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <AppButton
            color="inherit"
            onClick={() => setConfirmDialogOpen(false)}
            disabled={deleting}
          >
            Hủy
          </AppButton>
          <AppButton
            variant="contained"
            color="error"
            onClick={executeDeleteSubcontract}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {deleting ? "Đang xóa..." : "Xóa"}
          </AppButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
